import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { XIcon, CheckIcon, TableIcon, MailIcon, DocumentTextIcon, ChartBarIcon, PlusIcon } from '@heroicons/react/outline';
import { colors, spacing, typography } from '../../design-system/tokens';
import { Modal } from '../../design-system/components/feedback/Modal';
import { Input } from '../../design-system/components/inputs/Input';
import { TableType, TABLE_TYPES, canCreateTable, getTableTypeRequirements } from '../../types/tableTypes';
import { TABLE_TEMPLATES } from './tableTemplates';
import { PlatformIcon, OutlookIcon } from '../../design-system/components/data-display/EmailProviderIcons';

interface CreateTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; description?: string; table_type: TableType; context_required?: boolean }) => void;
  existingTemplates: string[];
}

// Template configuration for context requirements
const TEMPLATE_CONTEXT_CONFIG = {
  [TableType.CUSTOM]: { contextRequired: false, fixed: false }
};

const getTemplateIcon = (tableType: TableType) => {
  const iconMap = {
    [TableType.CUSTOM]: PlusIcon
  };
  return iconMap[tableType] || PlusIcon;
};

// Global styles to override modal defaults for create table modal
const CreateTableModalStyles = createGlobalStyle`
  .create-table-modal {
    background: ${colors.palette.gray[100]} !important;
    top: 2% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    height: 96% !important;
    max-height: 96% !important;
    
    /* Remove default modal body padding */
    & > div:last-child {
      padding: 0 !important;
    }
  }
`;

export const CreateTableModal: React.FC<CreateTableModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  existingTemplates
}) => {
  const [selectedType, setSelectedType] = useState<TableType | string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedType(null);
      setCreating(false);
    }
  }, [isOpen]);

  const handleTemplateSelect = (type: TableType | string) => {
    setSelectedType(type);
  };

  const generateTableName = (templateType: TableType | string) => {
    // Check if it's a workflow template
    const workflowTemplate = TABLE_TEMPLATES.find(t => t.id === templateType);
    if (workflowTemplate) {
      return workflowTemplate.name;
    }
    
    // Check if it's a TABLE_TYPE
    if (templateType in TABLE_TYPES) {
      return TABLE_TYPES[templateType as TableType].name;
    }
    return 'New Table';
  };

  const handleCreate = async () => {
    if (!selectedType) return;
    
    setCreating(true);
    try {
      const tableName = generateTableName(selectedType);
      let description = '';
      let contextRequired = false;
      
      // Check if it's a workflow template
      const workflowTemplate = TABLE_TEMPLATES.find(t => t.id === selectedType);
      if (workflowTemplate) {
        description = workflowTemplate.description;
        contextRequired = workflowTemplate.defaultConfig?.context_required || false;
      } else if (selectedType in TABLE_TYPES) {
        // Get description and context requirements from TABLE_TYPES
        description = TABLE_TYPES[selectedType as TableType].description;
        const contextConfig = TEMPLATE_CONTEXT_CONFIG[selectedType as TableType];
        contextRequired = contextConfig?.contextRequired || false;
      }
      
      await onCreate({
        name: tableName,
        description,
        table_type: selectedType as TableType,
        context_required: contextRequired
      });
      onClose();
    } finally {
      setCreating(false);
    }
  };

  const canCreate = selectedType !== null;

  return (
    <>
      <CreateTableModalStyles />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        height="full"
        showCloseButton={false}
        className="create-table-modal"
      >
        <ModalContent>
          <TopPanel>
            <TitleSection>
              <h3>Create Table</h3>
            </TitleSection>

            <ActionButtons>
              <SecondaryButton onClick={onClose} disabled={creating}>
                <XIcon />
                Cancel
              </SecondaryButton>
              <SaveButton 
                onClick={handleCreate} 
                disabled={!canCreate || creating}
              >
                {creating ? (
                  <>Creating...</>
                ) : (
                  <>
                    <CheckIcon />
                    Create Table
                  </>
                )}
              </SaveButton>
            </ActionButtons>
          </TopPanel>

          <ContentArea>
            <ContentSheet>
              {/* Workflow Templates Section */}
              <Section>
                <SectionHeader>
                  <SectionTitle>Workflow Templates</SectionTitle>
                </SectionHeader>

                <TemplateGrid>
                  {TABLE_TEMPLATES.map((template) => {
                    const IconComponent = template.icon;
                    const isSelected = selectedType === template.id;
                    const columns = template.defaultConfig?.columns || [];
                    
                    return (
                      <TemplateCard
                        key={template.id}
                        $selected={isSelected}
                        $disabled={false}
                        onClick={() => handleTemplateSelect(template.id)}
                      >
                        <IconContainer $category="workflow">
                          <IconComponent />
                        </IconContainer>
                        
                        <CardContent>
                          <CardHeader>
                            <CardTitle>{template.name}</CardTitle>
                            {isSelected && <SelectedIndicator><CheckIcon /></SelectedIndicator>}
                          </CardHeader>
                          
                          <CardDescription>{template.description}</CardDescription>
                          
                          {columns.length > 0 && (
                            <ColumnBadges>
                              {columns.slice(0, 4).map((col, idx) => (
                                <ColumnBadge key={idx}>
                                  {col.platform_resources?.[0]?.platform ? (
                                    <PlatformIcon platform={col.platform_resources[0].platform} size={9} />
                                  ) : col.email_provider === 'outlook' ? (
                                    <OutlookIcon size={9} />
                                  ) : null}
                                  {col.name}
                                </ColumnBadge>
                              ))}
                              {columns.length > 4 && (
                                <ColumnBadge>+{columns.length - 4} more</ColumnBadge>
                              )}
                            </ColumnBadges>
                          )}
                        </CardContent>
                      </TemplateCard>
                    );
                  })}
                </TemplateGrid>
              </Section>

              {/* Custom / From Scratch Section */}
              <Section>
                <SectionHeader>
                  <SectionTitle>Create Custom Table</SectionTitle>
                </SectionHeader>

                <TemplateGrid>
                  {Object.values(TABLE_TYPES).map((template) => {
                    const IconComponent = getTemplateIcon(template.id);
                    const isSelected = selectedType === template.id;
                    const canCreateThis = canCreateTable(template.id, existingTemplates);
                    
                    return (
                      <TemplateCard
                        key={template.id}
                        $selected={isSelected}
                        $disabled={!canCreateThis}
                        onClick={() => canCreateThis && handleTemplateSelect(template.id)}
                      >
                        <IconContainer $category={template.category}>
                          <IconComponent />
                        </IconContainer>
                        
                        <CardContent>
                          <CardHeader>
                            <CardTitle>{template.name}</CardTitle>
                            {isSelected && <SelectedIndicator><CheckIcon /></SelectedIndicator>}
                          </CardHeader>
                          
                          <CardDescription>{template.description}</CardDescription>
                        </CardContent>
                        
                        {!canCreateThis && (
                          <DisabledOverlay>
                            <span>Setup required</span>
                          </DisabledOverlay>
                        )}
                      </TemplateCard>
                    );
                  })}
                </TemplateGrid>
              </Section>
            </ContentSheet>
          </ContentArea>
        </ModalContent>
      </Modal>
    </>
  );
};

// Styled Components (matching CreatorModal design)
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TopPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.palette.gray[25]};
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);
`;

const TitleSection = styled.div`
  flex: 1;
  max-width: 300px;
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.text.primary};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing[1]};
  align-items: center;
`;

const SaveButton = styled.button`
  background: transparent;
  color: ${colors.text.primary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  background: ${colors.palette.gray[100]};
  padding: ${spacing[4]} ${spacing[8]} ${spacing[4]} ${spacing[8]};
  display: flex;
  justify-content: center;
  overflow-y: auto;
`;

const ContentSheet = styled.div`
  width: 100%;
  max-width: 1000px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  padding: ${spacing[6]};
  margin-bottom: ${spacing[4]};
  align-self: flex-start;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[6]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${spacing[3]};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const TemplateGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const TemplateCard = styled.div<{ $selected: boolean; $disabled: boolean }>`
  position: relative;
  padding: ${spacing[4]};
  border: none;
  border-radius: 6px;
  background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.06)' : colors.palette.gray[50]};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.12s ease;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};

  &:hover:not(:disabled) {
    background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.08)' : colors.palette.gray[100]};
  }

  ${props => props.$selected && `
    box-shadow: 0 0 0 1.5px ${colors.brand.primary} inset;
  `}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  flex: 1;
  min-width: 0;
`;

const IconContainer = styled.div<{ $category?: string }>`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  color: ${colors.text.tertiary};
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const CardTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  line-height: 1.3;
  margin-bottom: 4px;
`;

const SelectedIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background: ${colors.brand.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 10px;
    height: 10px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${colors.text.tertiary};
  line-height: 1.5;
`;

const ColumnBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`;

const ColumnBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 3px;
  font-size: 10px;
  color: ${colors.text.secondary};
  font-weight: 400;
  
  svg, img {
    width: 9px;
    height: 9px;
    flex-shrink: 0;
    opacity: 0.7;
  }
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  span {
    padding: 4px 10px;
    background: rgba(0, 0, 0, 0.05);
    color: ${colors.text.tertiary};
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
  }
`;